import { Select } from '@avanta/shared/components/ui/Select';
import { FLOW_MODES } from '@avanta/shared/constants/appointments';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { Building } from 'lucide-react';
import { useEffect } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ContractSelects } from 'pages/AppointmentsReserve/components/ui/ContractSelects';
import { RecordFields } from 'pages/AppointmentsReserve/components/ui/RecordFields';
import { SessionSummary } from 'pages/AppointmentsReserve/components/ui/SessionSummary';
import { StepBackButton } from 'pages/AppointmentsReserve/components/ui/StepBackButton';
import { StepNextButton } from 'pages/AppointmentsReserve/components/ui/StepNextButton';
import { StepSection } from 'pages/AppointmentsReserve/components/ui/StepSection';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/constants';
import { useFetchInsuredCenters } from 'pages/AppointmentsReserve/hooks/useFetchInsuredCenters';
import { useGetContractOptions } from 'pages/AppointmentsReserve/hooks/useGetContractOptions';
import { type StepProps, type AppointmentsReserveFormData } from 'pages/AppointmentsReserve/types';
import {
  areRecordFieldsComplete,
  resetAvailabilitySelection,
  resetContractSelection,
  setSelectedCenter,
} from 'pages/AppointmentsReserve/utils';
import { DataStepSkeleton } from './components/DataStepSkeleton';
import { NoScheduleBanner } from './components/NoScheduleBanner';
import { ReopenRecordBanner } from './components/ReopenRecordBanner';
import { useDataStepBanners } from './hooks/useDataStepBanners';

export const DataStep = ({ onNext, onBack }: StepProps): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.APPOINTMENTS);
  const {
    setValue,
    control,
    formState: { defaultValues },
  } = useFormContext<AppointmentsReserveFormData>();
  const [flow, contractId, contractRateId, recordFieldValues, backofficeCenterId, serviceBooked] =
    useWatch({
      control,
      name: [
        AppointmentsReservePayload.flow,
        AppointmentsReservePayload.contractId,
        AppointmentsReservePayload.contractRateId,
        AppointmentsReservePayload.recordFields,
        AppointmentsReservePayload.backofficeCenterId,
        AppointmentsReservePayload.psychologistServiceBooked,
      ],
    });
  const {
    contractOptions,
    rateOptions,
    recordFields,
    isLoading: isContractOptionsLoading,
  } = useGetContractOptions();
  const { centersOptions: centers, isLoading: isSessionDataLoading } = useFetchInsuredCenters();
  const {
    showNoScheduleBanner,
    showReopenRecordBanner,
    isLoading: isBannersLoading,
  } = useDataStepBanners();
  const singleContractId = contractOptions.length === 1 ? contractOptions[0].value : undefined;

  const isLoading = isContractOptionsLoading || isSessionDataLoading || isBannersLoading;

  useEffect(() => {
    if (isLoading || contractId || !singleContractId) return;
    resetAvailabilitySelection(setValue, flow);
    setValue(AppointmentsReservePayload.contractId, String(singleContractId));
  }, [contractId, flow, isLoading, setValue, singleContractId]);

  const handleContractIdChange = (value: string): void => {
    if (value === contractId) return;
    resetContractSelection(setValue, flow, value);
  };

  const handleRateChange = (value: string): void => {
    setValue(AppointmentsReservePayload.contractRateId, value);
  };

  const isValid =
    Boolean(contractId) &&
    Boolean(contractRateId) &&
    Boolean(serviceBooked) &&
    areRecordFieldsComplete(recordFields, recordFieldValues ?? {});

  if (isLoading) {
    return <DataStepSkeleton />;
  }

  const handleCenterChange = (value: string): void => {
    if (value === backofficeCenterId) return;
    resetAvailabilitySelection(setValue, flow);
    setSelectedCenter(setValue, value, centers.find(center => center.value === value)?.city ?? '');
  };

  const handleBack = (): void => {
    resetContractSelection(setValue, flow);
    onBack();
  };

  return (
    <div className="flex w-full max-w-241 flex-col gap-12">
      <div className="flex gap-8">
        <SessionSummary />
        <StepSection
          title={t('AppointmentsReserve.Steps.data.administrative.title')}
          className="grid grid-cols-2 gap-6"
          containerClassName="w-full flex-1"
        >
          <ContractSelects
            contractOptions={contractOptions}
            rateOptions={rateOptions}
            readOnlyContract={Boolean(defaultValues?.contractId)}
            readOnlyRate={Boolean(defaultValues?.contractRateId)}
            onContractChange={handleContractIdChange}
            onRateChange={handleRateChange}
          />
          {centers.length > 1 && (
            <Select
              label={t('AppointmentsReserve.Steps.session.center.label')}
              options={centers}
              placeholder={t('AppointmentsReserve.Steps.session.center.placeholder')}
              leftIcon={Building}
              value={backofficeCenterId}
              onValueChange={handleCenterChange}
            />
          )}
          {contractId && (
            <RecordFields
              control={control}
              recordFields={recordFields}
              readOnlyInitialValues={defaultValues?.recordFields}
            />
          )}
          {showNoScheduleBanner && <NoScheduleBanner />}
          {showReopenRecordBanner && <ReopenRecordBanner />}
        </StepSection>
      </div>
      <div
        className={cn(
          'flex w-full justify-end',
          flow === FLOW_MODES.search_patient && 'justify-between'
        )}
      >
        {flow === FLOW_MODES.search_patient && <StepBackButton onClick={handleBack} />}
        <StepNextButton disabled={!isValid} onClick={onNext} />
      </div>
    </div>
  );
};
